import { useState, useEffect } from "react";
import { HelpCircle, Mail, Send, Truck, RotateCcw, Shield, FileText, Cookie } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

function PageShell({ icon: Icon, eyebrow, title, children }) {
  useEffect(() => window.scrollTo(0, 0), []);
  return (
    <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8">
      <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#00F0FF]"><Icon className="h-4 w-4" /> {eyebrow}</div>
      <h1 className="font-display mt-2 text-3xl font-bold text-white sm:text-4xl">{title}</h1>
      <div className="mt-8 space-y-5 text-[15px] leading-relaxed text-slate-300">{children}</div>
    </div>
  );
}

function Prose({ sections }) {
  return sections.map((s, i) => (
    <div key={i} className="dw-card rounded-2xl p-6">
      <h2 className="font-display text-lg font-semibold text-white">{s.h}</h2>
      <p className="mt-2 text-slate-300">{s.p}</p>
    </div>
  ));
}

export function FAQ() {
  const faqs = [
    ["When will products actually be available to ship?", "Our full catalog is launching soon. Product stories and cover images are marked 'Coming Soon' while our global sourcing team finalizes details. You can still create orders which will be fulfilled as inventory goes live."],
    ["Which countries do you ship to?", "We ship to 190+ countries via our global fulfilment network, with TeamDrop as our preferred supplier and AliExpress as backup."],
    ["How is my currency determined?", "We auto-detect your region on arrival and display localized pricing. You can change currency anytime with the selector in the header. All orders settle in USD behind the scenes."],
    ["How long does delivery take?", "Standard worldwide shipping is typically 7–15 business days depending on destination. You'll receive tracking as soon as your order ships."],
    ["What payment methods do you accept?", "At launch we support PayPal and major credit cards. Checkout is currently in demo mode — no real charges are made."],
    ["How do I track my order?", "Use the Track page with your DW-XXXXXXXX order ID, or open any order from your account dashboard."],
  ];
  return (
    <PageShell icon={HelpCircle} eyebrow="Help Center" title="Frequently Asked Questions">
      <Accordion type="single" collapsible className="space-y-3">
        {faqs.map(([q, a], i) => (
          <AccordionItem key={i} value={`f${i}`} className="dw-card overflow-hidden rounded-2xl border-none px-6" data-testid={`faq-${i}`}>
            <AccordionTrigger className="text-left text-[15px] font-semibold text-slate-100 hover:no-underline">{q}</AccordionTrigger>
            <AccordionContent className="text-slate-400">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <div className="dw-card mt-4 flex items-center justify-between rounded-2xl p-6">
        <span>Still need help?</span>
        <a href="mailto:support@dropworldwide.com" className="font-mono text-sm text-[#00F0FF]">support@dropworldwide.com</a>
      </div>
    </PageShell>
  );
}

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent!");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  return (
    <PageShell icon={Mail} eyebrow="Get in touch" title="Contact & Support">
      <p>Our team replies within 24 hours. For urgent order issues, include your DW order ID. Or email <a href="mailto:support@dropworldwide.com" className="text-[#00F0FF]">support@dropworldwide.com</a>.</p>
      <form onSubmit={submit} className="dw-card space-y-4 rounded-2xl p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label className="text-slate-300">Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 border-white/10 bg-white/5" data-testid="contact-name" /></div>
          <div><Label className="text-slate-300">Email</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 border-white/10 bg-white/5" data-testid="contact-email" /></div>
        </div>
        <div><Label className="text-slate-300">Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5 border-white/10 bg-white/5" data-testid="contact-subject" /></div>
        <div><Label className="text-slate-300">Message</Label><Textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 border-white/10 bg-white/5" data-testid="contact-message" /></div>
        <Button type="submit" disabled={loading} className="rounded-xl bg-[#00F0FF] px-6 py-6 font-bold text-[#08090E] hover:bg-[#33F3FF]" data-testid="contact-submit"><Send className="mr-2 h-4 w-4" /> {loading ? "Sending…" : "Send Message"}</Button>
      </form>
    </PageShell>
  );
}

export const Shipping = () => (
  <PageShell icon={Truck} eyebrow="Policy" title="Global Shipping">
    <Prose sections={[
      { h: "Worldwide delivery", p: "We ship to 190+ countries through our global fulfilment network. Orders are routed to our preferred supplier TeamDrop, with AliExpress as backup for maximum availability." },
      { h: "Rates & free shipping", p: "Standard shipping is a flat $6.99 and FREE on all orders over $75. Rates are shown in your local currency at checkout." },
      { h: "Delivery times", p: "Standard worldwide shipping typically takes 7–15 business days. You'll receive a tracking number by email the moment your order ships." },
      { h: "Customs & duties", p: "Depending on your country, import duties or taxes may apply on delivery. These are set by your local authorities and are the recipient's responsibility." },
    ]} />
  </PageShell>
);

export const Returns = () => (
  <PageShell icon={RotateCcw} eyebrow="Policy" title="Returns & Refunds">
    <Prose sections={[
      { h: "30-day returns", p: "If you're not satisfied, request a return within 30 days of delivery for a full refund of the item price." },
      { h: "How to start a return", p: "Email support@dropworldwide.com with your order ID and reason. We'll send a prepaid label or return instructions." },
      { h: "Refund timing", p: "Refunds are issued to your original payment method within 5–10 business days after we receive the returned item." },
      { h: "Damaged or wrong items", p: "If your item arrives damaged or incorrect, contact us within 48 hours with photos and we'll ship a replacement at no cost." },
    ]} />
  </PageShell>
);

export const Privacy = () => (
  <PageShell icon={Shield} eyebrow="Legal" title="Privacy Policy">
    <Prose sections={[
      { h: "Information we collect", p: "We collect the details you provide (name, email, shipping address) and technical data such as approximate region for currency detection." },
      { h: "How we use it", p: "To process orders, route fulfilment to suppliers, send order and shipping updates, and improve your experience. We never sell your personal data." },
      { h: "Region detection", p: "We use your IP address only to estimate your country and default currency. You can override this any time via the currency selector." },
      { h: "Your rights", p: "You may request access, correction, or deletion of your data by emailing support@dropworldwide.com." },
    ]} />
  </PageShell>
);

export const Terms = () => (
  <PageShell icon={FileText} eyebrow="Legal" title="Terms of Service">
    <Prose sections={[
      { h: "Acceptance", p: "By using Drop Worldwide you agree to these terms. Products are currently marked 'Coming Soon' during our pre-launch phase." },
      { h: "Orders & pricing", p: "All prices are computed from a USD base and displayed in your selected currency. We reserve the right to correct pricing errors." },
      { h: "Demo checkout", p: "Checkout is currently operating in demo mode. No real payment is processed until live payment providers are connected." },
      { h: "Limitation of liability", p: "Drop Worldwide is not liable for indirect damages. Our liability is limited to the amount paid for the relevant order." },
    ]} />
  </PageShell>
);

export const Cookies = () => (
  <PageShell icon={Cookie} eyebrow="Legal" title="Cookie Policy">
    <Prose sections={[
      { h: "What we use cookies for", p: "Essential cookies keep your cart and session working. Preference cookies remember your currency selection." },
      { h: "Region & currency", p: "We store your chosen currency locally so your prices stay consistent between visits." },
      { h: "Managing cookies", p: "You can accept all or essential-only cookies via the banner, and clear them anytime in your browser settings." },
    ]} />
  </PageShell>
);
